import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Eye, Send, Save, Mail } from "lucide-react";
import { API } from "@/lib/auth";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TEMPLATES = [
  { k: "deposit_request", l: "Booking-fee request", when: "Sent when you tap “Email booking-fee link” on an enquiry. Includes the secure pay button." },
  { k: "date_secured", l: "Date secured confirmation", when: "Sent automatically to the bride the moment her booking fee is paid.", toggle: "date_secured_enabled" },
  { k: "trial_reminder", l: "Trial reminder", when: "Sent in the 7 days before her trial date — only to Confirmed brides who have paid the booking fee.", toggle: "reminders_enabled" },
  { k: "wedding_reminder", l: "Wedding reminder", when: "Sent in the 7 days before her wedding — only to Confirmed brides who have paid the booking fee.", toggle: "reminders_enabled" },
  { k: "balance_request", l: "Balance request", when: "Sent when you tap “Email balance link” on a confirmed bride. {amount} is her remaining balance." },
  { k: "balance_receipt", l: "Balance receipt", when: "Sent automatically to the bride the moment her balance is paid.", toggle: "balance_receipt_enabled" },
  { k: "review_request", l: "Thank-you & review request", when: "Sent a few days after the wedding to brides who paid the booking fee. The button links to your review page below.", toggle: "review_requests_enabled" },
  { k: "weekly_digest", l: "Your Monday heads-up", when: "Sent to you every Monday at 8am: each confirmed trial and wedding in the next 7 days with name, date, location, services, people, balance and your latest notes.", toggle: "weekly_digest_enabled" },
];
const TOGGLES = [
  ["date_secured_enabled", "Date-secured email to bride after payment"],
  ["reminders_enabled", "Trial & wedding reminders to brides (9am daily check)"],
  ["weekly_digest_enabled", "Monday heads-up email to me"],
  ["balance_receipt_enabled", "Receipt email to bride after balance payment"],
  ["review_requests_enabled", "Thank-you & review request after the wedding"],
];
const FIELD_LABEL = { subject: "Subject line", heading: "Headline", body: "Message", button: "Button text", intro: "Intro line" };
const inputCls = "w-full rounded-xl border border-[hsl(var(--onyx)/0.15)] bg-white px-3 py-2 text-sm";

function TemplateCard({ def, value, onChange, onPreview }) {
  return (
    <details data-testid={`bridal-template-${def.k}`} className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-5">
      <summary className="cursor-pointer list-none flex items-center justify-between gap-3">
        <span className="font-display text-xl inline-flex items-center gap-2"><Mail size={15} className="text-[hsl(var(--gold))]" /> {def.l}</span>
      </summary>
      <p className="mt-2 text-xs text-[hsl(var(--onyx)/0.55)]">{def.when}</p>
      <div className="mt-4 space-y-3">
        {Object.keys(value).map((f) => (
          <label key={f} className="block">
            <span className="text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--onyx)/0.55)]">{FIELD_LABEL[f] || f}</span>
            {f === "body" ? (
              <textarea data-testid={`bridal-template-${def.k}-${f}`} rows={9} className={`${inputCls} mt-1`} value={value[f]} onChange={(e) => onChange({ ...value, [f]: e.target.value })} />
            ) : (
              <input data-testid={`bridal-template-${def.k}-${f}`} className={`${inputCls} mt-1`} value={value[f]} onChange={(e) => onChange({ ...value, [f]: e.target.value })} />
            )}
          </label>
        ))}
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => onPreview(def.k, false)} data-testid={`bridal-template-${def.k}-preview`} className="text-xs px-3 py-2 rounded-full border inline-flex items-center gap-1.5"><Eye size={12} /> Preview</button>
          <button type="button" onClick={() => onPreview(def.k, true)} data-testid={`bridal-template-${def.k}-test`} className="text-xs px-3 py-2 rounded-full border inline-flex items-center gap-1.5"><Send size={12} /> Send test to me</button>
        </div>
      </div>
    </details>
  );
}

export default function BridalAutomations() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/bridal/automations`).then((r) => { setData(r.data); setDraft(r.data.settings); }).catch(() => toast.error("Could not load automations"));
  }, []);
  if (!draft) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin" size={20} /></div>;

  const dirty = JSON.stringify(draft) !== JSON.stringify(data.settings);
  const save = async (next = draft) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/bridal/automations`, next);
      setData(r.data); setDraft(r.data.settings); toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const toggle = (k, v) => {
    if (dirty && !window.confirm("You have unsaved template edits — save them too?")) return;
    save({ ...draft, [k]: v });
  };
  const runPreview = async (key, sendTest) => {
    try {
      const r = await axios.post(`${API}/admin/bridal/automations/preview`, { key, template: draft.templates[key], send_test: sendTest });
      if (sendTest) toast.success(`Test sent to ${r.data.sent_to}`);
      else setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
  };
  const runDigest = async () => {
    setDigestBusy(true);
    try { const r = await axios.post(`${API}/admin/bridal/automations/run-digest`); toast.success(`Heads-up sent (${r.data.items} booking${r.data.items === 1 ? "" : "s"})`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); } finally { setDigestBusy(false); }
  };

  return (
    <div data-testid="bridal-automations" className="space-y-4">
      <section className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-5 space-y-4">
        <div className="overline">Automations</div>
        <p className="text-xs text-[hsl(var(--onyx)/0.6)]">Everything starts switched off. Edit and preview each email below, then switch it on when you're happy. Reminders only ever go to brides marked Confirmed (or later) whose booking fee is paid — never to enquiries or unsecured dates.</p>
        {TOGGLES.map(([k, l]) => (
          <label key={k} className="flex items-center justify-between gap-4">
            <span className="text-sm">{l}</span>
            <Switch className="data-[state=unchecked]:bg-[hsl(var(--onyx)/0.25)]" data-testid={`bridal-toggle-${k}`} checked={!!draft[k]} disabled={saving} onCheckedChange={(v) => toggle(k, v)} />
          </label>
        ))}
        <label className="flex items-center justify-between gap-4">
          <span className="text-sm">Booking fee amount (£)</span>
          <input data-testid="bridal-deposit-amount-input" type="number" min={1} className={`${inputCls} max-w-[110px]`} value={draft.deposit_amount} onChange={(e) => setDraft({ ...draft, deposit_amount: e.target.value === "" ? "" : Number(e.target.value) })} />
        </label>
        <label className="flex items-center justify-between gap-4">
          <span className="text-sm">Send review request how many days after the wedding?</span>
          <input data-testid="bridal-review-delay-input" type="number" min={1} max={30} className={`${inputCls} max-w-[110px]`} value={draft.review_delay_days} onChange={(e) => setDraft({ ...draft, review_delay_days: e.target.value === "" ? "" : Number(e.target.value) })} />
        </label>
        <label className="block">
          <span className="text-sm">Review link (e.g. your Google review link)</span>
          <input data-testid="bridal-review-url-input" className={`${inputCls} mt-1`} value={draft.review_url} onChange={(e) => setDraft({ ...draft, review_url: e.target.value })} />
        </label>
        <button onClick={runDigest} disabled={digestBusy} data-testid="bridal-run-digest-btn" className="text-xs px-4 py-2 rounded-full border inline-flex items-center gap-1.5">
          {digestBusy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send me this week's heads-up now
        </button>
      </section>

      <div className="rounded-2xl bg-[hsl(var(--blush)/0.35)] p-4 text-xs text-[hsl(var(--onyx)/0.7)]" data-testid="bridal-placeholders">
        Personalise with: {data.placeholders.map((p) => <code key={p} className="mx-0.5 px-1.5 py-0.5 rounded bg-white">{`{${p}}`}</code>)}
        <span className="block mt-1">Leave a blank line between paragraphs. Your sign-off “With love, Malek” is added automatically.</span>
      </div>

      {TEMPLATES.map((def) => (
        <TemplateCard key={def.k} def={def} value={draft.templates[def.k]} onPreview={runPreview}
          onChange={(v) => setDraft({ ...draft, templates: { ...draft.templates, [def.k]: v } })} />
      ))}

      <div className="sticky bottom-4 flex justify-end">
        <button onClick={() => save()} disabled={!dirty || saving} data-testid="bridal-automations-save-btn" className="btn-pill btn-primary shadow-lg disabled:opacity-40">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save changes
        </button>
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} data-testid="bridal-template-preview-modal" className="max-w-2xl w-[95vw] p-4">
          <DialogHeader><DialogTitle className="text-sm font-medium">Subject: {preview?.subject}</DialogTitle></DialogHeader>
          <iframe title="Email preview" sandbox="" tabIndex={-1} srcDoc={preview?.html} className="w-full h-[70vh] rounded-xl border bg-white" />
          <p className="text-[11px] text-[hsl(var(--onyx)/0.5)]">Preview uses a sample bride (Sophie Campbell).</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
